import gql from 'graphql-tag';

const SURVEY_WITH_USER_ANSWERS_FRAGMENT: string = `
    id
    org_id
    start_date
    end_date
    title
    description
    instructions
    is_open
    campaign_id
    survey_sections {
        id
        section_name
        section_title
        section_subtitle
        section_index
        section_is_required
        questions(order_by: { created_at: asc }) {
            id
            question_type
            question_title
            question_subtitle
            answer_groups {
                id
                answer_group_name
                possible_answers {
                    id
                    answer_text
                    user_answers(where: { user_id: { _eq: $user_id } }) {
                        user_id
                        answer_id
                    }
                }
            }
        }
    }
`;

const SURVEY_FRAGMENT: string = `
    id
    org_id
    start_date
    end_date
    title
    description
    instructions
    is_open
    survey_sections {
        id
        section_name
        section_title
        section_subtitle
        section_index
        section_is_required
        questions(order_by: { created_at: asc }) {
            id
            question_index
            question_type
            question_title
            question_subtitle
            answer_groups {
                id
                answer_group_name
                possible_answers(order_by: { created_at: asc }) {
                    id
                    answer_group_id
                    answer_index
                    answer_text
                }
            }
        }
    }
    survey_type {
        base_type
        created_at
        description
        id
        title
        updated_at
    }
`;

const SURVEY_LIST_FRAGMENT: string = `
    id
    org_id
    start_date
    end_date
    title
    description
    instructions
    is_open
`;


const Q_GET_SURVEY = gql`
    query GetSurvey($survey_id: uuid, $base_type: String, $author_id: uuid) {
        surveys(where: { _and: [
                { id: { _eq: $survey_id }},
                { survey_type: { base_type: {_eq: $base_type }}},
                { author_id: { _eq: $author_id }}
            ]},
            order_by: { updated_at: desc }
        ) {
            ${SURVEY_FRAGMENT}
        }
    } 
`;

const Q_GET_SURVEY_AND_RESPONSE_COUNT = gql`
    query GetSurvey($survey_id: uuid, $author_id: uuid, $survey_type_id: Int, $limit: Int) {
        surveys(
            where: { _and: [
                { id: { _eq: $survey_id }},
                { survey_type_id: { _eq: $survey_type_id }},
                { author_id: { _eq: $author_id }}
            ]},
            order_by: { created_at: desc },
            limit: $limit
        ) {
            ${SURVEY_FRAGMENT}
            user_answers_aggregate(distinct_on: user_id) {
                aggregate {
                    count
                }
            }
            anon_user_answers_aggregate(distinct_on: user_id) {
                aggregate {
                    count
                }
            }
        }
    } 
`;

// used when a user is in the process of completing a survey/poll
const Q_GET_SURVEY_AND_USER_ANSWERS = gql`
    query GetSurveyAndAnswers($survey_id: uuid, $org_id: uuid, $user_id: uuid) {
        surveys(where: {
            _and: [
                { id: { _eq: $survey_id } },
                { org_id: { _eq: $org_id } }
            ]
        }) {
            ${SURVEY_WITH_USER_ANSWERS_FRAGMENT}
        }
    } 
`;

const Q_GET_SURVEY_USER_ANSWERS_AGGREGATE = gql`
    query get_survey_user_answer_count($survey_id: uuid!, $user_id: uuid!) {
        get_survey_user_answers_aggregate(args: { p_survey_id: $survey_id, p_user_id: $user_id }) {
            aggregate {
                count
            }
        }
    }
`;

const Q_SURVEY_RESULTS = gql`
    query SurveyResults($survey_id: uuid) {
        surveys(where: { id: { _eq: $survey_id } }) {
            id
            title
            is_open
            start_date
            end_date
            survey_sections {
                id
                questions {
                    id
                    question_index
                    question_title
                    question_type
                    answer_groups {
                        id
                        possible_answers {
                            id
                            answer_index
                            answer_text
                            user_answers_aggregate {
                                aggregate {
                                    count
                                }
                            }
                            anon_user_answers_aggregate {
                                aggregate {
                                    count
                                }
                            }
                        }
                    }
                }
            }
            user_answers_aggregate(distinct_on: user_id) {
                aggregate {
                    count
                }
            }
            anon_user_answers_aggregate(distinct_on: user_id) {
                aggregate {
                    count
                }
            }
        }
    }
`;

const Q_GET_SURVEY_PARTICIPANT_COUNT = gql`
    query GetSurveyParticipantCount($survey_id: uuid) {
        surveys(where: { id: { _eq: $survey_id } }) {
            user_answers_aggregate(distinct_on: user_id) {
                aggregate {
                    count
                }
            }
            anon_user_answers_aggregate(distinct_on: user_id) {
                aggregate {
                    count
                }
            }
        }
    }
`;

const Q_GET_SURVEY_PARTICIPANTS = gql`
    query SurveyParticipants($survey_id: uuid!) {
        surveys(where: { id: { _eq: $survey_id } }) {
            id
            title
            user_answers(distinct_on: user_id) {
                answer_text
                created_at
                user_id
                user {
                    username
                }
            }
            anon_user_answers(distinct_on: user_id) {
                answer_text
                created_at
                user_id
                user: anon_user {
                    username
                }
            }
        }
    }
`;

const Q_GET_SURVEY_QUESTION_RESPONSES = gql`
    query SurveyQuestionResponses($survey_id: uuid!, $question_id: uuid!) {
        surveys(where: { id: { _eq: $survey_id } }) {
            id
            title
            survey_sections {
                questions(where: { id: { _eq: $question_id } }) {
                    question_title
                    question_subtitle
                }
            }
            user_answers(where: { question_id: { _eq: $question_id } }) {
                id
                answer_text
                created_at
                user {
                    username
                }
            }
            anon_user_answers(where: { question_id: { _eq: $question_id } }) {
                id
                answer_text
                created_at
                user: anon_user {
                    username
                }
            }
        }
    }
`;


export {
    Q_GET_SURVEY,
    Q_GET_SURVEY_AND_RESPONSE_COUNT,
    Q_GET_SURVEY_AND_USER_ANSWERS,
    SURVEY_FRAGMENT,
    SURVEY_LIST_FRAGMENT,
    Q_GET_SURVEY_USER_ANSWERS_AGGREGATE, // temp: replace with hasura action
    Q_SURVEY_RESULTS,
    Q_GET_SURVEY_PARTICIPANT_COUNT,
    Q_GET_SURVEY_PARTICIPANTS,
    Q_GET_SURVEY_QUESTION_RESPONSES
}




